import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { AuthContext } from "../../../AuthContextWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { OUTCOMES } from "../../../constants";
import {
  SingleSelectButtonForm,
  SelectButton,
} from "../shared/SingleSelectButtonForm";
import { AngryIcon, OkIcon, SmileIcon } from "../shared/EmotionIcons";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  notTalking = "We're not talking",
  ok = "Ok",
  great = "Great",
}

const iconStyles = { width: 60, height: 60, padding: "6px" } as const;
const iconViewBox = "0 0 48 48";

const icons: { [key: string]: JSX.Element } = {
  notTalking: <AngryIcon sx={iconStyles} viewBox={iconViewBox} />,
  ok: <OkIcon sx={iconStyles} viewBox={iconViewBox} />,
  great: <SmileIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const responses: { [key: string]: string } = {
  notTalking:
    "No problem. Our advisors can help prepare and inform you so that you have the smoothest experience possible. ",
  ok: "Family and money can be hard. It’s good to know your options and what to do when inheriting assets—that's where we can help.",
  great:
    "That’s good to hear—families who get along can move through the process much faster and with less stress.",
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const feelingSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  feelingSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
    response: responses[key],
  })
);

export const BeneRelationshipsScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useContext(AuthContext);
  const { latestFinancialConvo, setLatestFinancialConvo } = useContext(
    FinancialConvoContext
  );

  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (
    relationshipStatus: string
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    const financialConvo = latestFinancialConvo || new FinancialConvo();
    financialConvo.beneRelationshipStatus = relationshipStatus;

    try {
      await financialConvo.saveRequest();
      setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper title="How are the relationships between the beneficiaries of the estate?">
      <SingleSelectButtonForm
        initChecked={latestFinancialConvo?.beneRelationshipStatus}
        selectButtons={feelingSelectButtons}
        required
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
